import type { HTMLAttributes, ReactNode, TableHTMLAttributes } from "react";
import { Icon } from "./icons";

// Table + status label + pagination — codified from 10-tablolar.html and
// 11-rozetler-etiketler.html. Table is a semantic <table> wearing the founder's chrome
// (sober header, roomy rows, tabular numerals). TableCard is the optional bordered
// frame (title row + footer). Everything is presentational + props-driven.

export interface TableCardProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  title?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
}

export function TableCard({ title, actions, footer, children, ...rest }: TableCardProps) {
  return (
    <div className="k-tablecard" {...rest}>
      {title || actions ? (
        <div className="k-tablecard__head">
          <div className="k-tablecard__title">{title}</div>
          {actions ? <div>{actions}</div> : null}
        </div>
      ) : null}
      {children}
      {footer ? <div className="k-tablecard__foot">{footer}</div> : null}
    </div>
  );
}

export function Table({ className, ...props }: TableHTMLAttributes<HTMLTableElement>) {
  return <table className={`k-table${className ? ` ${className}` : ""}`} {...props} />;
}

// Avatar chip (the round initials cell in member/policy tables).
export function Avatar({ children }: { children: ReactNode }) {
  return <span className="k-avatar">{children}</span>;
}

export type StatusTone = "active" | "review" | "expiring" | "pending" | "ended";

export interface StatusLabelProps {
  tone: StatusTone;
  children: ReactNode;
  /** The leading dot (default on) — the founder's at-a-glance status cue. */
  dot?: boolean;
}

export function StatusLabel({ tone, children, dot = true }: StatusLabelProps) {
  return (
    <span className={`k-status k-status--${tone}`}>
      {dot ? <span className="k-status__dot" /> : null}
      {children}
    </span>
  );
}

export interface PaginationProps {
  page: number;
  pageCount: number;
  onPage: (page: number) => void;
  /** e.g. "1.284 kayıttan 5 tanesi" — the caller composes the copy. */
  countLabel?: ReactNode;
  prevLabel?: string;
  nextLabel?: string;
}

export function Pagination({
  page,
  pageCount,
  onPage,
  countLabel,
  prevLabel = "Previous",
  nextLabel = "Next",
}: PaginationProps) {
  const pages = Array.from({ length: pageCount }, (_, i) => i + 1);
  return (
    <>
      {countLabel ? <div className="k-pager__count">{countLabel}</div> : <span />}
      <div className="k-pager">
        <button
          type="button"
          className="k-pager__btn"
          aria-label={prevLabel}
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          <Icon name="chevron-left" size={15} strokeWidth={2} />
        </button>
        {pages.map((p) => (
          <button
            key={p}
            type="button"
            aria-current={p === page ? "page" : undefined}
            className={`k-pager__btn${p === page ? " k-pager__btn--active" : ""}`}
            onClick={() => onPage(p)}
          >
            {p}
          </button>
        ))}
        <button
          type="button"
          className="k-pager__btn"
          aria-label={nextLabel}
          disabled={page >= pageCount}
          onClick={() => onPage(page + 1)}
        >
          <Icon name="chevron-right" size={15} strokeWidth={2} />
        </button>
      </div>
    </>
  );
}
